import { Col, Form, Row } from "antd";
import OrderPrestataireTable from "./OrderPrestataireTable";
import { useEffect, useMemo, useState } from "react";
import {
  useMergeState,
  usePriceTypes,
  usePriceTypesQuery,
  useCatalogPriceZoneServiceProvider,
  useCatalogPriceZoneServiceProviderQuery,
} from "hooks";
import { OrderPrestataireLine } from "./OrderPrestataireCellTable";
import { UrbanCenterWithZone } from "types";
import { v4 as uuidv4 } from "uuid";
import { DocumentProductLinePrice, Documents } from "models";
import { ReplaceNewSpType } from "index";

interface OrderPrestataireModalStates {
  collapsed: boolean;
  isLoading: boolean;
  selectValue: number | null;
  mainProductsName: string[] | undefined;
  isSelect: boolean;
  excludeServiceProviderIds: number[];
}

interface OrderPrestataireModalProps {
  expeditionId: number | null;
  orderDetail: Documents | null;
  orderServiceProvider: Documents | null;
  handleSubmitNewSP: (data: ReplaceNewSpType) => void;
  serviceProviderZoneIds: UrbanCenterWithZone[];
  selectedProductLines: {
    priceFamilyId?: number;
    productId?: number;
    quantity?: number;
    productNameForClient?: string;
    unitPrice?: number;
  }[];
  qboServiceProviderId: number | null;
}

const OrderPrestataireModal = (props: OrderPrestataireModalProps) => {
  const {
    expeditionId,
    orderDetail,
    orderServiceProvider,
    handleSubmitNewSP,
    serviceProviderZoneIds,
    selectedProductLines,
    qboServiceProviderId,
  } = props;
  const [form] = Form.useForm();
  const [selectedRowKey, setSelectedRowKey] = useState<number | null>(null);
  const [loadingFilter, setLoadingFilter] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<OrderPrestataireLine[]>([]);
  const [replaceInfo, setReplaceInfo] = useState<{
    replaceMode?: boolean;
    replaceIndex?: number;
    replaceServiceProviderId?: number;
    oldUuid?: string;
    oldProductId?: number;
    preservedQuantity?: number;
    moreProducts?: { quantity?: number }[];
  } | null>(null);

  const [state, setMergedState] = useMergeState<OrderPrestataireModalStates>({
    collapsed: false,
    isLoading: false,
    selectValue: null,
    mainProductsName: [],
    isSelect: false,
    excludeServiceProviderIds: [],
  });

  const [catalogPriceZoneServiceProviderQuery] =
    useCatalogPriceZoneServiceProviderQuery({
      qboServiceProviderId,
      serviceProviderZoneIds,
      products: selectedProductLines,
      siteAddressFull: orderDetail?.siteAddressFull,
      contactId: orderDetail?.contactId,
    });
  const [
    result,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    catalogPriceZoneServiceProviderRefresh,
    catalogPriceZoneServiceProvidersLoading,
  ] = useCatalogPriceZoneServiceProvider(catalogPriceZoneServiceProviderQuery);
  console.log("result: ", result);
  const catalogPriceZoneServiceProviders = result?.catalogPriceResult || [];
  const selectedProducts = result?.selectedProducts || [];

  const [effectSPIdsLoading, setEffectSPIdsLoading] = useState<number[]>([]);

  const [priceTypesQuery] = usePriceTypesQuery();
  const [priceTypes] = usePriceTypes(priceTypesQuery);

  const orderSpDocumentProductLineIds =
    orderServiceProvider?.DocumentProductLines?.map(
      (documentProductLine) => documentProductLine.id
    ) || [];
  const orderClientDocumentProductLines =
    orderDetail?.DocumentProductLines || [];
  const selectedProductsFromOrderClient = useMemo(() => {
    return orderClientDocumentProductLines
      .filter(
        (row) =>
          row?.MasterProductLines &&
          row?.MasterProductLines?.length > 0 &&
          row?.MasterProductLines?.find(
            (masterProductLine) =>
              masterProductLine.derivedProductLineId &&
              orderSpDocumentProductLineIds.includes(
                masterProductLine.derivedProductLineId
              )
          )
      )
      .map((row) => ({
        productId: row.productId,
        ...(row?.priceFamilyId ? { priceFamilyId: row.priceFamilyId } : {}),
        ...(row?.productNameForClient
          ? { productNameForClient: row.productNameForClient }
          : {}),
        ...(row?.quantity ? { quantity: row.quantity } : {}),
        ...(row?.id ? { id: row.id } : {}),
        ...(row?.DocumentProductLinePrices
          ? { DocumentProductLinePrices: row.DocumentProductLinePrices }
          : {}),
        ...(row?.unitPrice ? { unitPrice: row.unitPrice } : {}),
        total: row?.total ?? 0,
      }));
  }, [orderClientDocumentProductLines]);

  useEffect(() => {
    catalogPriceZoneServiceProviderRefresh({
      qboServiceProviderId,
      serviceProviderZoneIds,
      products: selectedProductLines,
      siteAddressFull: orderDetail?.siteAddressFull,
      contactId: orderDetail?.contactId,
    });
  }, [selectedProductLines, serviceProviderZoneIds, qboServiceProviderId]);

  useEffect(() => {
    if (catalogPriceZoneServiceProviders.length > 0) {
      const providersWithUUID = catalogPriceZoneServiceProviders.map(
        (item) => ({
          ...item,
          products: item.products.map((product) => ({
            ...product,
            uuid: uuidv4(),
          })),
          isNew: false,
        })
      );
      const uniqueProductNames = [
        ...new Set(
          providersWithUUID
            .flatMap((item) => item.products.map((p) => p.productName))
            .filter((name): name is string => typeof name === "string")
        ),
      ];
      setDataSource(providersWithUUID);
      setMergedState({
        excludeServiceProviderIds: providersWithUUID.map(
          (item) => item.serviceProviderId
        ),
        mainProductsName: uniqueProductNames,
      });
    }
  }, [catalogPriceZoneServiceProviders]);

  const onSelectRow = (row: OrderPrestataireLine) => {
    const documentProductLines = row.products.map((product) => {
      const selectedProductLine = selectedProductLines.find(
        (p) => p.priceFamilyId === product.selectedPriceFamilyId
      );
      const selectedPriceFamily = product.priceFamilies?.find(
        (pf) => pf.id === product.selectedPriceFamilyId
      );
      const priceFamilyPrices = selectedPriceFamily?.prices || [];
      const productLinePrices: DocumentProductLinePrice[] = [];
      if (
        selectedProductLine &&
        product?.documentProductLinePrices &&
        product?.documentProductLinePrices.length > 0
      ) {
        if (selectedPriceFamily) {
          const documentProductLinePriceFamily =
            product.documentProductLinePrices.find(
              (documentProductLinePrice) =>
                !documentProductLinePrice.priceId &&
                !documentProductLinePrice.priceOptionId
            );
          productLinePrices.push({
            ...documentProductLinePriceFamily,
            value: selectedPriceFamily.priceValue,
          });
        }
        product.documentProductLinePrices.map((documentProductLinePrice) => {
          const selectedPrice = priceFamilyPrices?.find(
            (p) => p.id === documentProductLinePrice.priceId
          );
          if (
            documentProductLinePrice.priceId &&
            !documentProductLinePrice.priceOptionId
          ) {
            productLinePrices.push({
              ...documentProductLinePrice,
              value: selectedPrice?.priceValue,
              isManualPrice: selectedPrice?.isManualPrice,
            });
          } else if (
            documentProductLinePrice.priceId &&
            documentProductLinePrice.priceOptionId
          ) {
            const priceFamilyPriceOptions = selectedPrice?.priceOptions || [];
            if (priceFamilyPriceOptions && priceFamilyPriceOptions.length > 0) {
              return priceFamilyPriceOptions.map((priceOption) => {
                if (priceOption.id === documentProductLinePrice.priceOptionId) {
                  return productLinePrices.push({
                    ...documentProductLinePrice,
                    value: priceOption.priceValue,
                    isManualPrice: false,
                  });
                }
              });
            }
          }
        });
      } else {
        if (selectedPriceFamily) {
          productLinePrices.push({
            value: selectedPriceFamily.priceValue,
          });
        }
        if (priceFamilyPrices && priceFamilyPrices.length > 0) {
          priceFamilyPrices.map((price) => {
            productLinePrices.push({
              priceId: price.id,
              priceLabel: price.name ?? "",
              value: price.priceValue,
              isManualPrice: price.isManualPrice,
            });
            const priceFamilyPriceOptions = price?.priceOptions || [];
            if (priceFamilyPriceOptions && priceFamilyPriceOptions.length > 0) {
              priceFamilyPriceOptions.map((priceOption) => {
                productLinePrices.push({
                  priceId: price.id,
                  priceOptionId: priceOption.id,
                  priceOptionLabel: priceOption.name ?? "",
                  value: priceOption.priceValue,
                  isManualPrice: false,
                });
              });
            }
          });
        }
      }
      const isManualPrice = product.isManualPrice === true;
      return {
        id: product.documentProductLineId,
        productId: product.productId,
        priceFamilyId: product.selectedPriceFamilyId,
        priceFamilyValue: selectedPriceFamily?.priceValue,
        quantity: product.quantity,
        unitPrice: product.priceValue,
        total: Number(product.priceValue) * (product.quantity ?? 0),
        priceMargin: product.priceMargin,
        isManualPrice,
        DocumentProductLinePrices: productLinePrices,
      };
    });
    console.log("documentProductLines: ", documentProductLines);
    const catalogPriceZoneServiceProvider =
      catalogPriceZoneServiceProviders &&
        catalogPriceZoneServiceProviders.length > 0
        ? catalogPriceZoneServiceProviders[0]
        : null;
    const selectedZones = catalogPriceZoneServiceProvider?.zones || [];
    const zone = selectedZones && selectedZones.length > 0 ? selectedZones[0] : null;
    // return;
    handleSubmitNewSP({
      expeditionId,
      serviceProviderId: row.serviceProviderId,
      siteAddressZoneId: zone?.zoneId ?? null,
      documentProductLines,
    });
  };

  const handleAssociateProducts = (
    selectedProducts: {
      uuid: string;
      productId: number;
      productName: string;
      quantity: number;
    }[]
  ) => {
    if (replaceInfo?.replaceMode && replaceInfo.replaceIndex !== undefined) {
      if (selectedProducts.length > 0) {
        setDataSource((prevDataSource) => {
          const updatedDataSource = prevDataSource.map((item) => {
            if (
              item.serviceProviderId === replaceInfo.replaceServiceProviderId
            ) {
              const updatedProducts = [...item.products];
              const replaceIndex = replaceInfo.replaceIndex!;
              if (replaceIndex >= 0 && replaceIndex < updatedProducts.length) {
                updatedProducts[replaceIndex] = {
                  ...updatedProducts[replaceIndex],
                  quantity: replaceInfo.preservedQuantity ?? 1,
                };
              }
              return { ...item, products: updatedProducts };
            }
            return item;
          });
          return updatedDataSource;
        });
      }
      // No action needed if no association (quantity stays 1)
      setReplaceInfo(null);
    } else if (selectedProducts.length > 0) {
      setDataSource((prevDataSource) => {
        const updatedDataSource = prevDataSource.map((item) => ({
          ...item,
          products: [
            ...item.products,
            ...selectedProducts.map((product) => ({
              ...product,
              priceValue: "0",
              priceFamilies: [],
              selectedPriceFamilyId: null,
              comment: null,
            })),
          ],
        }));
        return updatedDataSource;
      });
    }
  };

  const formatPrice = (price: number | undefined | null): string => {
    if (price === undefined || price === null) {
      return "00,00 €";
    }
    if (Number.isInteger(price)) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    }
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  return (
    <Form
      layout="horizontal"
      form={form}
      className="order-prestataire-modal-form"
    >
      <Row gutter={[16, 0]}>
        <Col xs={8}>
          <div style={{ marginBottom: 15, maxWidth: 350 }}></div>
        </Col>
        <Col xs={16}>
          <Row gutter={[16, 0]} className="client-address">
            <Col xs={10}>
              <div className="client-address__title">Commande Client</div>
              <div>
                <label>
                  <u>
                    <strong>Addresse chantier</strong>
                  </u>
                </label>
                <div>{orderDetail?.siteAddressFull}</div>
              </div>
            </Col>
            <Col xs={13} className="product-list">
              {selectedProductsFromOrderClient.map((item, index) => (
                <Row key={index} gutter={0} className="product-list__row">
                  <Col
                    xs={20}
                    className="product-list__row-item"
                    style={{ fontWeight: 500 }}
                  >
                    {item.productNameForClient}
                  </Col>
                  <Col
                    xs={4}
                    className="product-list__row-price"
                    style={{ fontWeight: 500 }}
                  >
                    {formatPrice(item.unitPrice)}
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        </Col>
      </Row>
      <div
        className={`prestataire-modal-body`}
        style={{ display: state.collapsed ? "none" : "block" }}
      >
        <OrderPrestataireTable
          form={form}
          dataSource={dataSource}
          setDataSource={setDataSource}
          setLoadingFilter={setLoadingFilter}
          isLoading={
            catalogPriceZoneServiceProvidersLoading === "pending" ||
            loadingFilter
          }
          priceTypes={priceTypes}
          onSelectRow={onSelectRow}
          selectedRowKey={selectedRowKey}
          setSelectedRowKey={setSelectedRowKey}
          serviceProviderZoneIds={serviceProviderZoneIds}
          effectSPIdsLoading={effectSPIdsLoading}
          setEffectSPIdsLoading={setEffectSPIdsLoading}
          selectedProductLines={selectedProductLines}
          onAssociateProducts={handleAssociateProducts}
          selectedProductsFromOrderClient={selectedProducts}
        />
      </div>
    </Form>
  );
};

export default OrderPrestataireModal;
